import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import {
  CopyIcon,
  DownloadIcon,
  UploadIcon,
  DeleteIcon,
  EditIcon,
  XIcon,
  SaveIcon,
} from '../components/icons';

/**
 * Read-only list pages inside a project.
 *
 * All four surfaces (Tours, Content, Themes, Checklists) follow the
 * same shape: fetch `/v1/admin/<thing>`, render a table with name +
 * status + version + updated timestamp.  The admin overlay is still
 * where authoring happens; this view is the "what exists on this
 * tenant/project" snapshot.
 *
 * Each page hands a small `columns` map to ResourceList to describe
 * the columns that are specific to it.  Everything else (loading,
 * empty, error, status badge) is shared.
 */

export function Tours() {
  const { slug } = useParams();
  return (
    <ResourceList
      path="/v1/admin/tours"
      title="Tours"
      subtitle={
        <>
          Multi-step product tours scoped to this project. Click a row
          to drill into its steps, URL patterns, and version history.
          Authoring still lives in the admin overlay — launch it from
          the Bookmarklet tab.
        </>
      }
      emptyTitle="No tours yet"
      emptyBody="Create one from the admin overlay's Tours panel on a page running your SDK."
      rowTo={(t) => `/projects/${slug}/tours/${encodeURIComponent(t.tour_id)}`}
      columns={[
        { header: 'Title', cell: (t) => t.title },
        { header: 'Steps', cell: (t) => (t.steps?.length ?? 0) },
        { header: 'Status', cell: (t) => <StatusBadge status={t.status} /> },
        { header: 'Version', cell: (t) => `v${t.version}` },
        { header: 'Updated', cell: (t) => <TimeCell iso={t.updated_at} /> },
      ]}
    />
  );
}

export function Content() {
  const { slug } = useParams();
  return (
    <ResourceList
      path="/v1/admin/content"
      title="Content"
      subtitle={
        <>
          Single-piece content: spotlights, tooltips, hotspots, modals,
          banners. Click a row for the full config + a visual preview
          of how it renders.
        </>
      }
      emptyTitle="No content yet"
      emptyBody="Add spotlights, tooltips, or banners from the admin overlay's Content panel."
      rowTo={(c) => `/projects/${slug}/content/${encodeURIComponent(c.content_id)}`}
      columns={[
        { header: 'Title', cell: (c) => c.title },
        { header: 'Type', cell: (c) => <code>{c.content_type}</code> },
        { header: 'Status', cell: (c) => <StatusBadge status={c.status} /> },
        { header: 'Priority', cell: (c) => c.priority ?? 0 },
        { header: 'Updated', cell: (c) => <TimeCell iso={c.updated_at} /> },
      ]}
    />
  );
}

export function Themes() {
  const { slug } = useParams();
  return <ThemesView slug={slug} />;
}

function ThemesView({ slug }) {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );
  const navigate = useNavigate();

  const [themes, setThemes] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPreset, setPendingPreset] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Request both project-scoped themes AND tenant-wide globals
      // (project_id="" rows).  Backend's list endpoint applies the
      // scoping ladder when project_id is set.
      const projectQuery = project?.project_id
        ? `?project_id=${encodeURIComponent(project.project_id)}`
        : '';
      const [themesData, presetsData] = await Promise.all([
        api(`/v1/admin/themes${projectQuery}`),
        api('/v1/admin/themes/presets').catch(() => ({ items: [] })),
      ]);
      const list = Array.isArray(themesData) ? themesData : (themesData?.items || []);
      setThemes(list);
      setPresets(presetsData?.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, project?.project_id]);

  useEffect(() => { load(); }, [load]);

  const defaultTheme = themes.find((t) => t.is_default);
  const others = themes.filter((t) => !t.is_default);

  async function handleSetDefault(themeId) {
    setActionError(null);
    try {
      await api(`/v1/admin/themes/${encodeURIComponent(themeId)}/set-default`, { method: 'POST' });
      await load();
    } catch (e) { setActionError(e.message); }
  }

  async function handleDelete(theme) {
    if (theme.is_default) {
      setActionError("Can't delete the default theme — set another theme as default first.");
      return;
    }
    if (theme.is_shared) {
      setActionError("Built-in shared themes can't be deleted.");
      return;
    }
    ask({
      title: `Delete theme "${theme.name}"?`,
      body: "This can't be undone.",
      confirmLabel: 'Delete',
      danger: true,
      runOnConfirm: async () => {
        setActionError(null);
        try {
          await api(`/v1/admin/themes/${encodeURIComponent(theme.theme_id)}`, { method: 'DELETE' });
          await load();
        } catch (e) {
          setActionError(e.message);
          throw e;
        }
      },
    });
  }

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Themes</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          CSS-variable palettes applied to every tooltip / modal / banner /
          spotlight across this project. Click a row to edit colours and radii
          with a live preview; "Set as default" makes it the tenant's active palette.
        </p>
      </div>

      {actionError && (
        <div className="state state--error">{actionError}</div>
      )}

      {loading && <div className="state"><div className="state__title">Loading…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        <>
          {defaultTheme && (
            <DefaultThemeHero
              theme={defaultTheme}
              onEdit={() => navigate(`/projects/${slug}/themes/${encodeURIComponent(defaultTheme.theme_id)}`)}
            />
          )}

          {others.length > 0 && (
            <div>
              <h3 className="card__title" style={{ marginBottom: 8 }}>
                {defaultTheme ? 'Other themes' : 'Your themes'}
              </h3>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="resource-table">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Preview</th>
                      <th>Name</th>
                      <th>Id</th>
                      <th>Updated</th>
                      <th style={{ width: 1 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {others.map((t) => {
                      const isGlobal = !t.project_id;
                      const inThisProject = t.project_id === project?.project_id;
                      const isDefault = t.is_default;
                      return (
                        <tr
                          key={t.theme_id}
                          className="resource-table__row--link"
                          onClick={(e) => {
                            if (e.target.closest('a,button,input,select')) return;
                            navigate(`/projects/${slug}/themes/${encodeURIComponent(t.theme_id)}`);
                          }}
                        >
                          <td><ThemeSwatch variables={themeVars(t)} /></td>
                          <td>
                            <strong>{t.name}</strong>
                            {t.is_shared && <span className="badge badge--viewer" style={{ marginLeft: 8 }}>Shared</span>}
                            {isGlobal && !t.is_shared && (
                              <span className="badge badge--editor" style={{ marginLeft: 8 }} title="Available to every project in this tenant">Global</span>
                            )}
                            {!isGlobal && !inThisProject && <span className="badge badge--default" style={{ marginLeft: 8 }}>Other project</span>}
                          </td>
                          <td><code>{t.theme_id}</code></td>
                          <td><TimeCell iso={t.updated_at} /></td>
                          <td>
                            <div className="theme-row__icons">
                              <IconButton
                                title={isDefault ? 'This is the active theme' : 'Make this the active theme'}
                                onClick={() => !isDefault && handleSetDefault(t.theme_id)}
                                disabled={isDefault}
                                tone={isDefault ? 'active' : undefined}
                              >
                                {/* star — set as default; filled when already
                                    default, outline otherwise.  One-off
                                    glyph so it stays inline rather than
                                    joining the shared vocab. */}
                                <svg
                                  width="14" height="14" viewBox="0 0 24 24"
                                  fill={isDefault ? 'currentColor' : 'none'}
                                  stroke="currentColor" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round"
                                  aria-hidden="true" focusable="false"
                                >
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                </svg>
                              </IconButton>
                              <IconButton
                                title={`Make a copy in ${project?.name || 'this project'}`}
                                onClick={async () => {
                                  const cloneName = window.prompt(`Copy "${t.name}" — new name?`, `${t.name} (copy)`);
                                  if (cloneName === null) return;
                                  try {
                                    await api(`/v1/admin/themes/${encodeURIComponent(t.theme_id)}/copy`, {
                                      method: 'POST',
                                      body: JSON.stringify({
                                        name: cloneName.trim(),
                                        project_id: project?.project_id || '',
                                      }),
                                    });
                                    await load();
                                  } catch (e) { setActionError(e.message); }
                                }}
                              >
                                <CopyIcon />
                              </IconButton>
                              {!t.is_shared && (
                                <IconButton
                                  title={
                                    isGlobal
                                      ? `Move this theme into ${project?.name || 'the current project'} (it'll stop showing on other projects)`
                                      : 'Promote this theme to tenant-wide (becomes a Global theme visible to every project)'
                                  }
                                  onClick={async () => {
                                    const target = isGlobal ? project?.project_id || '' : '';
                                    try {
                                      await api(`/v1/admin/themes/${encodeURIComponent(t.theme_id)}`, {
                                        method: 'PUT',
                                        body: JSON.stringify(target ? { project_id: target } : { clear_project_id: true }),
                                      });
                                      await load();
                                    } catch (e) { setActionError(e.message); }
                                  }}
                                >
                                  {isGlobal
                                    /* "Move into project" — download into the
                                       project tray. */
                                    ? <DownloadIcon />
                                    /* "Promote to global" — push it upward to
                                       the tenant level. */
                                    : <UploadIcon />}
                                </IconButton>
                              )}
                              <IconButton
                                title={t.is_shared ? 'Shared themes are read-only' : `Delete "${t.name}"`}
                                disabled={t.is_shared}
                                tone="danger"
                                onClick={() => handleDelete(t)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!defaultTheme && others.length === 0 && (
            <div className="state">
              <div className="state__title">No themes yet</div>
              <div>Pick a preset below to start.</div>
            </div>
          )}

          {presets.length > 0 && (
            <div>
              <h3 className="card__title" style={{ marginBottom: 6 }}>Start from a preset</h3>
              <p className="page__subtitle" style={{ marginTop: 0, marginBottom: 10 }}>
                Six tasteful starting points. Click a preset to preview, then save
                with a name of your choice — the clone is yours to edit freely.
              </p>
              <div className="preset-grid">
                {presets.map((p) => (
                  <PresetCard
                    key={p.id}
                    preset={p}
                    onClick={() => setPendingPreset(p)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {pendingPreset && (
        <CreateFromPresetModal
          preset={pendingPreset}
          api={api}
          onCancel={() => setPendingPreset(null)}
          onCreated={(created) => {
            setPendingPreset(null);
            navigate(`/projects/${slug}/themes/${encodeURIComponent(created.theme_id)}`);
          }}
        />
      )}

      {confirmNode}

      <style>{THEMES_CSS}</style>
    </section>
  );
}

function themeVars(theme) {
  return theme?.light?.variables || theme?.variables || {};
}

function IconButton({ children, title, onClick, disabled, tone }) {
  return (
    <button
      type="button"
      className={`icon-btn${tone ? ` icon-btn--${tone}` : ''}`}
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(e); }}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function ThemeSwatch({ variables }) {
  const bg = variables['--spot-bg'] || '#fff';
  const border = variables['--spot-border'] || '#e5e7eb';
  const primary = variables['--spot-primary'] || '#4f46e5';
  const accent = variables['--spot-hotspot-color'] || primary;
  return (
    <div className="preset-card__swatch" style={{ background: bg, borderColor: border, minHeight: 32 }}>
      <span className="preset-card__chip" style={{ background: primary }} />
      <span className="preset-card__chip preset-card__chip--alt" style={{ background: accent }} />
    </div>
  );
}

function DefaultThemeHero({ theme, onEdit }) {
  const vars = themeVars(theme);
  // Render a faux tooltip/card using the theme's own variables so
  // the operator can see at-a-glance how the active palette feels.
  return (
    <div className="theme-hero" style={{
      '--hero-bg': vars['--spot-bg'] || '#ffffff',
      '--hero-fg': vars['--spot-text'] || vars['--spot-fg'] || '#0f172a',
      '--hero-muted': vars['--spot-text-muted'] || '#64748b',
      '--hero-primary': vars['--spot-primary'] || '#4f46e5',
      '--hero-primary-fg': vars['--spot-primary-foreground'] || '#ffffff',
      '--hero-border': vars['--spot-border'] || '#e5e7eb',
      '--hero-radius': vars['--spot-radius'] || '10px',
      '--hero-accent': vars['--spot-hotspot-color'] || vars['--spot-primary'] || '#4f46e5',
    }}>
      <div className="theme-hero__meta">
        <span className="badge badge--default">Default</span>
        <h3 className="theme-hero__name">{theme.name}</h3>
        <div className="theme-hero__id"><code>{theme.theme_id}</code></div>
        <p className="theme-hero__desc">
          This is what end users see right now. Edit to tweak palette and radii;
          changes apply to every tooltip, modal, banner, hotspot and spotlight.
        </p>
        <button type="button" className="theme-hero__edit" onClick={onEdit}>
          <EditIcon /> Edit default theme
        </button>
      </div>
      <div className="theme-hero__preview">
        <div className="theme-hero__card">
          <div className="theme-hero__card-title">Welcome to your dashboard</div>
          <div className="theme-hero__card-body">
            This banner inherits every token your default theme defines.
          </div>
          <div className="theme-hero__card-actions">
            <button type="button" className="theme-hero__cta">Get started</button>
            <span className="theme-hero__dot" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>
  );
}

function PresetCard({ preset, onClick }) {
  return (
    <button type="button" className="preset-card" onClick={onClick}>
      <ThemeSwatch variables={preset.variables} />
      <div className="preset-card__name">{preset.name}</div>
      <div className="preset-card__desc">{preset.description}</div>
      <div className="preset-card__cta">Preview &amp; save →</div>
    </button>
  );
}

function CreateFromPresetModal({ preset, api, onCancel, onCreated }) {
  const [name, setName] = useState(`${preset.name} theme`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setErr('Name is required.'); return; }
    setBusy(true);
    setErr(null);
    try {
      const created = await api('/v1/admin/themes', {
        method: 'POST',
        body: JSON.stringify({ name: trimmed, variables: preset.variables }),
      });
      onCreated(created);
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  }

  return (
    <div className="theme-modal__backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
      <form
        className="theme-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="theme-modal__head">
          <h3 className="theme-modal__title">Save preset as a new theme</h3>
          <button type="button" className="theme-modal__close" onClick={onCancel} aria-label="Close" title="Close">
            <XIcon size={18} />
          </button>
        </div>
        <div className="theme-modal__body">
          <div className="theme-modal__preview">
            <ThemeSwatch variables={preset.variables} />
            <div>
              <div className="theme-modal__preset-name">{preset.name}</div>
              <div className="theme-modal__preset-desc">{preset.description}</div>
            </div>
          </div>
          <label className="theme-modal__label">
            <span>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={100}
              className="theme-modal__input"
              placeholder="e.g. Spring 2026 palette"
            />
          </label>
          {err && <div className="state state--error">{err}</div>}
        </div>
        <div className="theme-modal__foot">
          <button type="button" className="theme-row__btn" onClick={onCancel} disabled={busy}>
            <XIcon /> Cancel
          </button>
          <button type="submit" className="theme-row__btn theme-row__btn--primary" disabled={busy}>
            <SaveIcon /> {busy ? 'Saving…' : 'Save theme'}
          </button>
        </div>
      </form>
    </div>
  );
}

const THEMES_CSS = `
  .preset-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
  .preset-card { text-align: left; padding: 12px; background: var(--surface-0,#fff); border: 1px solid var(--border-1,#e5e7eb); border-radius: 10px; cursor: pointer; font: inherit; color: inherit; display: flex; flex-direction: column; gap: 6px; transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease; }
  .preset-card:hover:not(:disabled) { transform: translateY(-1px); border-color: var(--brand-600,#4f46e5); box-shadow: 0 10px 24px -16px var(--brand-600,#4f46e5); }
  .preset-card:disabled { opacity: 0.5; cursor: progress; }
  .preset-card__swatch { display: flex; gap: 6px; align-items: center; padding: 10px; border: 1px solid; border-radius: 8px; min-height: 38px; }
  .preset-card__chip { width: 22px; height: 22px; border-radius: 50%; }
  .preset-card__chip--alt { width: 14px; height: 14px; }
  .preset-card__name { font-weight: 600; color: var(--text-1, #0f172a); font-size: 13px; }
  .preset-card__desc { font-size: 12px; color: var(--text-3, #64748b); line-height: 1.4; }
  .preset-card__cta { margin-top: auto; font-size: 12px; font-weight: 600; color: var(--brand-600, #4f46e5); }

  .theme-row__actions { display: flex; gap: 6px; justify-content: flex-end; white-space: nowrap; }
  .theme-row__btn { font: inherit; font-size: 12px; padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border-1); background: var(--surface-0); cursor: pointer; color: var(--text-1); transition: background .1s; }
  .theme-row__btn:hover:not(:disabled) { background: var(--surface-1); }
  .theme-row__btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .theme-row__btn--danger:hover:not(:disabled) { background: #fef2f2; border-color: #fca5a5; color: #b91c1c; }
  .theme-row__btn--primary { background: var(--brand-600,#4f46e5); border-color: var(--brand-600,#4f46e5); color: #fff; }
  .theme-row__btn--primary:hover:not(:disabled) { background: var(--brand-700,#4338ca); }

  .theme-hero { display: grid; grid-template-columns: minmax(220px, 1fr) minmax(240px, 1.2fr); gap: 16px; padding: 18px; border: 1px solid var(--border-1); border-radius: 12px; background: var(--surface-0); align-items: center; }
  @media (max-width: 640px) { .theme-hero { grid-template-columns: 1fr; } }
  .theme-hero__meta { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
  .theme-hero__name { margin: 4px 0 0; font-size: 18px; }
  .theme-hero__id { font-size: 11.5px; color: var(--text-3); }
  .theme-hero__desc { margin: 4px 0 6px; font-size: 13px; color: var(--text-3); line-height: 1.5; }
  .theme-hero__edit { font: inherit; font-size: 13px; font-weight: 600; padding: 8px 12px; border-radius: 8px; border: 1px solid var(--brand-600,#4f46e5); background: var(--brand-600,#4f46e5); color: #fff; cursor: pointer; }
  .theme-hero__edit:hover { background: var(--brand-700,#4338ca); }
  .theme-hero__preview { padding: 18px; background: var(--hero-bg); border: 1px dashed var(--hero-border); border-radius: var(--hero-radius); color: var(--hero-fg); }
  .theme-hero__card-title { font-weight: 700; font-size: 15px; margin-bottom: 4px; color: var(--hero-fg); }
  .theme-hero__card-body { font-size: 13px; color: var(--hero-muted); line-height: 1.45; margin-bottom: 12px; }
  .theme-hero__card-actions { display: flex; gap: 10px; align-items: center; }
  .theme-hero__cta { font: inherit; font-size: 12.5px; font-weight: 600; padding: 7px 12px; border-radius: var(--hero-radius); border: none; background: var(--hero-primary); color: var(--hero-primary-fg); cursor: default; }
  .theme-hero__dot { width: 10px; height: 10px; border-radius: 50%; background: var(--hero-accent); display: inline-block; }

  .theme-modal__backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
  .theme-modal { background: var(--surface-0, #fff); border-radius: 12px; width: 100%; max-width: 460px; max-height: calc(100vh - 32px); overflow: auto; box-shadow: 0 24px 48px -16px rgba(15, 23, 42, 0.4); display: flex; flex-direction: column; }
  .theme-modal__head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--border-1); }
  .theme-modal__title { margin: 0; font-size: 15px; }
  .theme-modal__close { font: inherit; font-size: 22px; line-height: 1; padding: 0 6px; background: transparent; border: 0; color: var(--text-3); cursor: pointer; }
  .theme-modal__body { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
  .theme-modal__preview { display: flex; gap: 12px; align-items: center; padding: 10px; border: 1px solid var(--border-1); border-radius: 10px; background: var(--surface-1); }
  .theme-modal__preset-name { font-weight: 600; font-size: 13.5px; color: var(--text-1); }
  .theme-modal__preset-desc { font-size: 12px; color: var(--text-3); line-height: 1.4; margin-top: 2px; }
  .theme-modal__label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; font-weight: 600; color: var(--text-1); }
  .theme-modal__input { font: inherit; padding: 8px 10px; border: 1px solid var(--border-1); border-radius: 8px; background: var(--surface-0); color: var(--text-1); }
  .theme-modal__input:focus { outline: 2px solid var(--brand-600, #4f46e5); outline-offset: 1px; }
  .theme-modal__foot { display: flex; gap: 8px; justify-content: flex-end; padding: 12px 18px; border-top: 1px solid var(--border-1); background: var(--surface-1); }

  .theme-row__icons { display: inline-flex; gap: 2px; justify-content: flex-end; }
  .icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; padding: 0; background: transparent; border: 1px solid transparent; border-radius: 8px; color: var(--text-3); cursor: pointer; transition: background .1s, color .1s, border-color .1s; }
  .icon-btn:hover:not(:disabled) { background: var(--surface-1); color: var(--text-1); border-color: var(--border-1); }
  .icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .icon-btn--active { color: var(--brand-600, #4f46e5); background: var(--brand-50, #eef2ff); border-color: var(--brand-200, #c7d2fe); }
  .icon-btn--danger:hover:not(:disabled) { background: #fef2f2; color: #b91c1c; border-color: #fca5a5; }
`;

export function Checklists() {
  const { slug } = useParams();
  return (
    <ResourceList
      path="/v1/admin/checklists"
      title="Checklists"
      subtitle={
        <>
          Persistent lists of onboarding tasks. Items can link to the
          page that completes them, and the SDK ticks them off as users
          hit the target events.  Click a row to preview how the
          checklist renders.
        </>
      }
      emptyTitle="No checklists yet"
      emptyBody="Create one from the admin overlay's Checklists panel."
      rowTo={(c) => `/projects/${slug}/checklists/${encodeURIComponent(c.checklist_id)}`}
      columns={[
        { header: 'Title', cell: (c) => c.title },
        { header: 'Items', cell: (c) => (c.items?.length ?? 0) },
        { header: 'Status', cell: (c) => <StatusBadge status={c.status} /> },
        { header: 'Updated', cell: (c) => <TimeCell iso={c.updated_at} /> },
      ]}
    />
  );
}

/**
 * Shared list skeleton: loads the collection, renders the table or
 * one of the empty / loading / error states.
 */
function ResourceList({ path, title, subtitle, columns, emptyTitle, emptyBody, rowTo }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  // Parent <ProjectDetail> feeds the loaded project via outlet
  // context so we can include X-Spotlight-Project on every request.
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api(path);
      const list = Array.isArray(data) ? data : (data?.items || []);
      setRows(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, path]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>{title}</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>{subtitle}</p>
      </div>

      {loading && (
        <div className="state"><div className="state__title">Loading…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load</div>
          <div>{error}</div>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="state">
          <div className="state__title">{emptyTitle}</div>
          <div>{emptyBody}</div>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="resource-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.header}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const href = rowTo ? rowTo(row) : null;
                const rowClass = href ? 'resource-table__row--link' : '';
                const content = columns.map((col) => (
                  <td key={col.header}>{col.cell(row)}</td>
                ));
                const key = row.tour_id || row.content_id || row.theme_id || row.checklist_id || i;
                return href ? (
                  <tr
                    key={key}
                    className={rowClass}
                    onClick={(e) => {
                      // Don't hijack clicks on real anchors/buttons
                      // inside the row if we ever add them.
                      if (e.target.closest('a,button,input,select')) return;
                      // Use react-router's navigate so the BrowserRouter
                      // basename (driven by VITE_BASE_PATH) is applied.
                      // The old `window.location.assign('/dashboard'+href)`
                      // hardcoded the docker-compose path prefix and
                      // 404'd through to the SPA fallback on Cloudflare
                      // Pages (deployed at the apex of the custom domain).
                      navigate(href);
                    }}
                  >
                    {content}
                  </tr>
                ) : (
                  <tr key={key}>{content}</tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <style>{RESOURCE_CSS}</style>
    </section>
  );
}

function StatusBadge({ status }) {
  const normalised = (status || '').toLowerCase();
  const cls =
    normalised === 'published' ? 'badge--editor'
    : normalised === 'draft' ? 'badge--admin'
    : normalised === 'archived' ? 'badge--default'
    : 'badge--viewer';
  return <span className={`badge ${cls}`}>{status || 'unknown'}</span>;
}

function TimeCell({ iso }) {
  if (!iso) return <span style={{ color: 'var(--text-3)' }}>—</span>;
  try {
    const d = new Date(iso);
    return (
      <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
        {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        {' '}
        {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </span>
    );
  } catch { return iso; }
}

const RESOURCE_CSS = `
  .resource-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .resource-table th,
  .resource-table td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid var(--border-1);
    vertical-align: middle;
  }
  .resource-table th {
    font-weight: 600;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-3);
    background: var(--surface-1);
  }
  .resource-table tbody tr:last-child td {
    border-bottom: none;
  }
  .resource-table tbody tr:hover {
    background: var(--surface-1);
  }
  .resource-table__row--link {
    cursor: pointer;
  }
  .resource-table__row--link:hover {
    background: var(--brand-50);
  }
`;
